import { useState } from "react";
import type { ReactElement } from "react";
import type { ApiClient, IncomingInvoice } from "@kundeninfo/api-client";
import { Eingabe } from "@kundeninfo/ui/me-design";
import { errorProp } from "../../components/fieldErrors.js";
import { ActionQuestion, PaymentQuestion } from "../suppliers/shared.js";
import { CANCEL_REASON_MAX } from "./shared.js";
import { INCOMING_TEXT_WEB as T } from "./texts.js";
import "./IncomingInvoices.css";

/**
 * DIE ZWEI DIREKTEN AKTIONEN an einer erfassten Eingangsrechnung (Spec „Akte eines Lieferanten“; Dirk 216c: gefragt wird
 * vor direkten Aktionen, nicht vor dem Speichern eines Panels). Beide sind die kleine gestapelte Frage der
 * Lieferantenansichten (`ActionQuestion`, suppliers/shared.tsx), die während ihrer Aktion offen bleibt und einen
 * Fehlschlag in sich zeigt:
 *
 *  - **Zahlung bestätigen** ist die gemeinsame Zahlungsfrage (`PaymentQuestion`, wie am Posten eines Vertrags): die EINE
 *    Zahlung, gesendet als Summe mit dem bisher Gezahlten (Pflichtpunkt W1 aus Review 13 — eine Teilzahlung überschreibt
 *    nie die vorige); Betrag 0 ist der Widerspruch. Mit Vorzeichen: Ein offenes Guthaben wird negativ bestätigt.
 *  - **Stornieren** fragt nach dem Grund; ohne Grund bleibt der Knopf gesperrt (Pflichtfeld, höchstens 500 Zeichen).
 *
 * Nach einem Fehlschlag lädt das Schließen die Liste neu (`onStale`, Fix-Runde 1 W2). Ein veralteter Stand (409
 * Versionskonflikt) oder eine Fachregel (422, etwa „storniert“) sperrt zudem den Knopf.
 */
type ActionProps = {
  customerId: string; invoice: IncomingInvoice; onGespeichert: (r: IncomingInvoice) => void; onSchliessen: () => void;
  /** Nach einem Fehlschlag statt `onSchliessen`: schließen und den aktuellen Stand laden. */
  onStale: () => void;
};

export function ZahlungPanel(p: ActionProps & { client: Pick<ApiClient, "confirmIncomingPayment"> }): ReactElement {
  const inv = p.invoice;
  return (
    <PaymentQuestion currency={inv.currency} paidAmount={inv.paidAmount} payment={inv.payment} gross={inv.payableAmount}
      signed onDone={p.onGespeichert} onClose={p.onSchliessen} onStale={p.onStale}
      run={(paidOn, paidAmount) => p.client.confirmIncomingPayment(p.customerId, inv.id,
        { version: inv.version, paidOn, paidAmount })} />
  );
}

export function CancelQuestion(p: ActionProps & { client: Pick<ApiClient, "cancelIncomingInvoice"> }): ReactElement {
  const inv = p.invoice;
  const [reason, setReason] = useState("");
  return (
    <ActionQuestion title={T.confirmCancel(inv.entryLabel)} confirmText={T.cancelAction} danger ready={reason.trim() !== ""}
      fields={["reason"]} onDone={p.onGespeichert} onClose={p.onSchliessen} onStale={p.onStale}
      run={() => p.client.cancelIncomingInvoice(p.customerId, inv.id, { version: inv.version, reason: reason.trim() })}>
      {(errors) => (
        <>
          <p className="inc__muted">{T.cancelConsequence}</p>
          <Eingabe beschriftung={T.cancelReason} mehrzeilig wert={reason} pflicht maxLength={CANCEL_REASON_MAX}
            {...errorProp(errors.reason)} onGeaendert={(e) => setReason(e.wert)} />
        </>
      )}
    </ActionQuestion>
  );
}
